import {
  BIO_MAX_VISIBLE_CHARS,
  BIO_MIN_VISIBLE_CHARS,
  bioVisibleTextLength,
  TExpertiseItem,
  toBioHtml,
  TUpdateProDirectory,
  UpdateProDirectorySchema,
} from '@onesource/schemas';
import { isEqual } from 'lodash-es';
import { Path } from 'react-hook-form';
import { IconType } from 'react-icons';
import { MdBusiness, MdPerson, MdSchool, MdVisibility } from 'react-icons/md';
import { z } from 'zod';

export const PROFESSION_MAX = 10;
export const DEFAULT_LANGUAGES_TAUGHT = ['English'];
export const TAB_BG_COLOR = 'rgb(244, 239, 238)';

/** `pro_directory_profession` row "Others" — selecting it reveals the free-text Other Profession field. */
export const OTHER_PROFESSION_ID = '38';

export const isOtherProfessionSelected = (profession: unknown): boolean =>
  Array.isArray(profession) && profession.includes(OTHER_PROFESSION_ID);

/**
 * Turns stored `expertise` values into display names.
 *
 * The column holds a mix: stringified `pro_directory_expertise` ids for anything saved
 * since the lookup table landed, and free text for older rows that were deliberately
 * not backfilled. Unmatched entries pass through unchanged so legacy values stay visible.
 * The API resolves this server-side for public profiles; this covers the form/revision
 * surfaces that read the raw stored values.
 */
export const resolveExpertiseNames = (values: string[] | null | undefined, items: TExpertiseItem[]): string[] => {
  const names = new Map(items.map(item => [String(item.id), item.name]));
  return (values ?? []).map(value => names.get(value) ?? value);
};

// Slug was removed from the member form (the backend mints it on submit) — omit it
// from the form's schema/type entirely so it's never part of RHF state, baseline
// hydration, or dirty-checking. See commit "Auto-generate Pro Directory slug on
// backend, remove field from member form".
export const ProFormSchema = UpdateProDirectorySchema.omit({ slug: true }).superRefine((value, ctx) => {
  if (!value.profession || value.profession.length === 0) {
    ctx.addIssue({
      path: ['profession'],
      code: z.ZodIssueCode.custom,
      message: 'Pick at least one profession',
    });
  }
  if (value.profession && value.profession.length > PROFESSION_MAX) {
    ctx.addIssue({
      path: ['profession'],
      code: z.ZodIssueCode.custom,
      message: `Select up to ${PROFESSION_MAX} professions`,
    });
  }
  if (!value.fullName?.trim()) {
    ctx.addIssue({
      path: ['fullName'],
      code: z.ZodIssueCode.custom,
      message: 'Full name is required',
    });
  }
  if (!value.title?.trim()) {
    ctx.addIssue({
      path: ['title'],
      code: z.ZodIssueCode.custom,
      message: 'Title is required',
    });
  }
  if (!value.casualImage) {
    ctx.addIssue({
      path: ['casualImage'],
      code: z.ZodIssueCode.custom,
      message: 'Casual image is required',
    });
  }
  if (!value.landscapeImage) {
    ctx.addIssue({
      path: ['landscapeImage'],
      code: z.ZodIssueCode.custom,
      message: 'Landscape image is required',
    });
  }
  const bioLength = bioVisibleTextLength(value.bio);
  if (bioLength > BIO_MAX_VISIBLE_CHARS) {
    ctx.addIssue({
      path: ['bio'],
      code: z.ZodIssueCode.custom,
      message: `Bio must be ${BIO_MAX_VISIBLE_CHARS} characters or fewer`,
    });
  }
  if (bioLength > 0 && bioLength < BIO_MIN_VISIBLE_CHARS) {
    ctx.addIssue({
      path: ['bio'],
      code: z.ZodIssueCode.custom,
      message: `Bio must be at least ${BIO_MIN_VISIBLE_CHARS} characters`,
    });
  }
});

export type TProFormValues = z.infer<typeof ProFormSchema>;

/**
 * `professionalWebsite` accepts a bare domain (example.com) so the field reads
 * as optional/friendly rather than requiring users to type a scheme. Stored and
 * linked values still need one, so it's added here rather than at entry time.
 */
export const normalizeWebsiteUrl = (value: string | null | undefined): string | null | undefined => {
  if (!value) {
    return value;
  }
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

// Phone is optional and the form auto-fills the country dial code (e.g. "+1") when
// a business country is picked. Treat an empty value or a bare dial code (no
// subscriber digits) as "not entered" so it isn't stored, previewed, or diffed.
export const normalizePhone = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  if (trimmed === '' || /^\+\d{1,4}$/.test(trimmed.replace(/\s+/g, ''))) {
    return null;
  }
  return trimmed;
};

/** Maps form values to the API payload shared by the member submit and the admin edit. */
export const toUpdateProDirectoryPayload = (data: Partial<TProFormValues>, isDraft: boolean): TUpdateProDirectory => ({
  isDraft,
  bio: data.bio,
  profession: data.profession,
  // Only meaningful alongside the "Others" profession — clear it otherwise so a
  // stale value can't survive the user deselecting "Others".
  otherProfession: isOtherProfessionSelected(data.profession) ? data.otherProfession : null,
  companyName: data.companyName,
  companyEmail: data.companyEmail,
  professionalWebsite: normalizeWebsiteUrl(data.professionalWebsite),
  offerings: data.offerings,
  teachingFormat: data.teachingFormat,
  expertise: data.expertise,
  languagesTaught: data.languagesTaught,
  casualImage: data.casualImage,
  landscapeImage: data.landscapeImage,
  fullName: data.fullName,
  title: data.title,
  phone: normalizePhone(data.phone),
  businessStreet: data.businessStreet,
  businessCountryId: data.businessCountryId,
  businessStateId: data.businessStateId,
  businessCityId: data.businessCityId,
  certificates: data.certificates,
  socials: data.socials,
});

export type TProfileSocials = {
  facebook: string;
  instagram: string;
  youtube: string;
  twitter: string;
  linkedin: string;
  tiktok: string;
};

/** The member's own account profile, used by the "Copy from My Account" controls. */
export type TProDirectoryAccountProfile = {
  fullName: string;
  bio: string;
  phone: string;
  socials: TProfileSocials;
  avatar: string;
};

// ── Baseline hydration + real dirty check ──────────────────────────────────
// RHF's isDirty compares against defaultValues and can get stuck dirty after an
// edit is manually reverted (array refs, normalized values, forced setValue with
// shouldDirty). We instead build the loaded baseline once and diff the current
// form values against it, so "edit then revert" correctly reads as not dirty.

const EMPTY_SOCIALS = { facebook: '', instagram: '', youtube: '', twitter: '', linkedin: '', tiktok: '' };

const socialsOf = (s: Record<string, string | null | undefined> | null | undefined) => ({
  facebook: s?.facebook ?? '',
  instagram: s?.instagram ?? '',
  youtube: s?.youtube ?? '',
  twitter: s?.twitter ?? '',
  linkedin: s?.linkedin ?? '',
  tiktok: s?.tiktok ?? '',
});

/** Form baseline from a revision's snake_case `data` blob. */
export const formValuesFromRevisionData = (d: Record<string, unknown>): TProFormValues =>
  ({
    bio: toBioHtml(d.bio as string | null),
    profession: (d.profession as string[]) ?? [],
    otherProfession: (d.other_profession as string) ?? '',
    companyName: (d.company_name as string) ?? '',
    companyEmail: (d.company_email as string) ?? '',
    professionalWebsite: (d.professional_website as string) ?? '',
    offerings: (d.offerings as string[]) ?? [],
    teachingFormat: (d.teaching_format as TProFormValues['teachingFormat']) ?? 'both',
    expertise: (d.expertise as string[]) ?? [],
    languagesTaught: (d.languages_taught as string[])?.length
      ? (d.languages_taught as string[])
      : DEFAULT_LANGUAGES_TAUGHT,
    casualImage: (d.casual_image as string) ?? '',
    landscapeImage: (d.landscape_image as string) ?? '',
    fullName: (d.full_name as string) ?? '',
    title: (d.title as string) ?? '',
    phone: (d.phone as string) ?? '',
    businessStreet: (d.business_street as string) ?? '',
    businessCountryId: (d.business_country_id as number) ?? null,
    businessStateId: (d.business_state_id as number) ?? null,
    businessCityId: (d.business_city_id as number) ?? null,
    certificates: (d.certificates as string) ?? '',
    socials: socialsOf(d.socials as Record<string, string> | null),
  }) as TProFormValues;

/** Form baseline from an approved pro-directory (camelCase) record. */
export const formValuesFromProDirectory = (p: Record<string, unknown>): TProFormValues =>
  ({
    bio: toBioHtml(p.bio as string | null),
    profession: (p.profession as string[]) ?? [],
    otherProfession: (p.otherProfession as string) ?? '',
    companyName: (p.companyName as string) ?? '',
    companyEmail: (p.companyEmail as string) ?? '',
    professionalWebsite: (p.professionalWebsite as string) ?? '',
    offerings: (p.offerings as string[]) ?? [],
    teachingFormat: (p.teachingFormat as TProFormValues['teachingFormat']) ?? 'both',
    expertise: (p.expertise as string[]) ?? [],
    languagesTaught: (p.languagesTaught as string[])?.length
      ? (p.languagesTaught as string[])
      : DEFAULT_LANGUAGES_TAUGHT,
    casualImage: (p.casualImage as string) ?? '',
    landscapeImage: (p.landscapeImage as string) ?? '',
    fullName: (p.fullName as string) ?? '',
    title: (p.title as string) ?? '',
    phone: (p.phone as string) ?? '',
    businessStreet: (p.businessStreet as string) ?? '',
    businessCountryId: (p.businessCountryId as number) ?? null,
    businessStateId: (p.businessStateId as number) ?? null,
    businessCityId: (p.businessCityId as number) ?? null,
    certificates: (p.certificates as string) ?? '',
    socials: socialsOf(p.socials as Record<string, string> | null),
  }) as TProFormValues;

/** The empty-form baseline (matches useForm defaultValues) for a brand-new profile. */
export const EMPTY_FORM_VALUES: TProFormValues = {
  bio: '',
  profession: [],
  otherProfession: '',
  companyName: '',
  companyEmail: '',
  professionalWebsite: '',
  offerings: [],
  teachingFormat: 'both',
  expertise: [],
  languagesTaught: DEFAULT_LANGUAGES_TAUGHT,
  casualImage: '',
  landscapeImage: '',
  fullName: '',
  title: '',
  phone: '',
  businessStreet: '',
  businessCountryId: null,
  businessStateId: null,
  businessCityId: null,
  certificates: '',
  socials: { ...EMPTY_SOCIALS },
} as TProFormValues;

/** Normalize one form value for comparison: trim strings, treat '' / null / undefined alike. */
const normalizeForCompare = (v: unknown): unknown => {
  if (typeof v === 'string') {
    // Treat empty/whitespace-only strings the same as null so '' vs null vs
    // undefined never counts as a change.
    return v.trim() === '' ? null : v.trim();
  }
  if (Array.isArray(v)) {
    // Every array field on this form (profession, offerings, expertise,
    // languagesTaught) is a set — selection order is not meaningful. Sort so
    // deselecting then reselecting an item (which appends it) doesn't read as a
    // change.
    return v.map(normalizeForCompare).sort((a, b) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1));
  }
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      out[k] = normalizeForCompare(val);
    }
    return out;
  }
  return v ?? null;
};

/**
 * Collapse an empty phone or a bare auto-filled dial code to null, WITHOUT
 * treating a real (short) number as empty. The auto-fill produces exactly
 * "+<code>" with no separator; once a user types a subscriber number there is a
 * space or more digits, so we only null out `+` followed by 1–4 digits and
 * nothing else.
 */
const dialCodeOnlyToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  if (trimmed === '' || /^\+\d{1,4}$/.test(trimmed)) {
    return null;
  }
  return trimmed;
};

/**
 * True when the current form values differ from the loaded baseline. Arrays are
 * compared as sets (order-insensitive) and empties are normalized, so reverting
 * an edit back to the original — including deselect/reselect — clears "dirty".
 */
export const isFormDirty = (current: Partial<TProFormValues>, baseline: TProFormValues): boolean => {
  const keys = Object.keys(baseline) as Array<keyof TProFormValues>;
  return keys.some(key => {
    // Phone: the form auto-fills a bare dial code (e.g. "+1") when a country is
    // picked, so a cleared phone still holds "+1" while the baseline is empty.
    // Treat a bare dial code (a leading "+digits" with NO separator/subscriber
    // digits after it) as empty for the comparison, so clearing a phone that only
    // held the auto-filled code reads as not dirty — without swallowing real
    // numbers like "+1 555 ...".
    if (key === 'phone') {
      return dialCodeOnlyToNull(current[key] as string | null) !== dialCodeOnlyToNull(baseline[key] as string | null);
    }
    return !isEqual(normalizeForCompare(current[key]), normalizeForCompare(baseline[key]));
  });
};

export const isFieldFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

export const REQUIRED_FIELDS: Array<{ key: keyof TProFormValues; label: string; step: number }> = [
  { key: 'fullName', label: 'Full Name', step: 0 },
  { key: 'casualImage', label: 'Profile Image', step: 0 },
  { key: 'landscapeImage', label: 'Landscape Image', step: 0 },
  { key: 'title', label: 'Title', step: 0 },
  { key: 'bio', label: 'Bio', step: 0 },
  { key: 'profession', label: 'Profession', step: 0 },
  { key: 'languagesTaught', label: 'Languages Taught', step: 1 },
  { key: 'teachingFormat', label: 'Teaching Format', step: 1 },
  { key: 'expertise', label: 'Expertise', step: 1 },
  { key: 'businessCountryId', label: 'Country', step: 2 },
  { key: 'businessStateId', label: 'State / Region', step: 2 },
  { key: 'businessCityId', label: 'City', step: 2 },
];

/** Human labels for fields that can fail format validation without being required. */
export const VALIDATED_FIELD_LABELS: Partial<Record<Path<TProFormValues>, string>> = {
  bio: 'Bio',
  companyEmail: 'Business Email',
  professionalWebsite: 'Business Website',
  'socials.facebook': 'Facebook',
  'socials.instagram': 'Instagram',
  'socials.youtube': 'Youtube',
  'socials.twitter': 'X',
  'socials.linkedin': 'Linkedin',
  'socials.tiktok': 'Tik Tok',
};

/** Walks a dotted RHF path (e.g. `socials.facebook`) into a nested `formState.errors` object. */
export const getFieldError = (errors: unknown, path: string): { message?: string } | undefined => {
  let node: unknown = errors;
  for (const part of path.split('.')) {
    if (!node || typeof node !== 'object') {
      return undefined;
    }
    node = (node as Record<string, unknown>)[part];
  }
  return node as { message?: string } | undefined;
};

/** A single outstanding issue surfaced in the footer help popover — either a required field left empty, or a filled field that fails format validation. */
export type TFormIssue = { kind: 'missing' | 'invalid'; message: string; step: number };

/** Which step (index into STEP_FIELDS/STEPS) a validated-but-not-required field lives on. */
export const getValidatedFieldStep = (path: string): number =>
  STEP_FIELDS.findIndex(fields => (fields as string[]).includes(path));

export const getMissingFields = (values: Partial<TProFormValues>): string[] =>
  REQUIRED_FIELDS.filter(f => !isFieldFilled(values[f.key])).map(f => f.label);

/** Same as `getMissingFields`, but keeps each field's step so callers can show where to fix it. */
export const getMissingFieldsWithStep = (values: Partial<TProFormValues>): Array<{ label: string; step: number }> =>
  REQUIRED_FIELDS.filter(f => !isFieldFilled(values[f.key])).map(f => ({ label: f.label, step: f.step }));

export const getStepMissingFields = (values: Partial<TProFormValues>, step: number): string[] =>
  REQUIRED_FIELDS.filter(f => f.step === step && !isFieldFilled(values[f.key])).map(f => f.label);

export const isStepComplete = (values: Partial<TProFormValues>, step: number): boolean =>
  getStepMissingFields(values, step).length === 0;

export const isProProfileComplete = (values: Partial<TProFormValues>): boolean => getMissingFields(values).length === 0;

export type TStepConfig = {
  label: string;
  heading: string;
  subtitle: string;
  icon: IconType;
};

export const STEPS: TStepConfig[] = [
  {
    label: 'About Me',
    heading: 'Personal details',
    subtitle: 'How you show up on your public profile.',
    icon: MdPerson,
  },
  {
    label: 'What I Offer',
    heading: 'Teaching Information',
    subtitle: 'What you teach and how you teach it.',
    icon: MdSchool,
  },
  {
    label: 'How to Find Me',
    heading: 'Business information',
    subtitle: 'Where clients can find and contact you.',
    icon: MdBusiness,
  },
  {
    label: 'Preview',
    heading: 'Profile preview',
    subtitle: 'Review your profile before submitting it for review.',
    icon: MdVisibility,
  },
];

export const STEP_FIELDS: Array<Array<Path<TProFormValues>>> = [
  ['casualImage', 'landscapeImage', 'fullName', 'title', 'bio', 'profession', 'otherProfession', 'certificates'],
  ['languagesTaught', 'offerings', 'teachingFormat', 'expertise'],
  [
    'companyName',
    'companyEmail',
    'professionalWebsite',
    'phone',
    'businessStreet',
    'businessCountryId',
    'businessStateId',
    'businessCityId',
    'socials.facebook',
    'socials.instagram',
    'socials.youtube',
    'socials.twitter',
    'socials.linkedin',
    'socials.tiktok',
  ],
  [],
];
